import React from "react";
import { useFragment } from "react-relay";
import { match } from "ts-pattern";
import { useTranslation } from "@i18n/hooks";
import { type orderHistoryEvent_OrderHistoryFragment$key } from "@relay/orderHistoryEvent_OrderHistoryFragment.graphql";
import { ORDER_HISTORY_FRAGMENT } from "./order-history-event.graphql";
import { type OrderHistoryEventProps } from "./order-history-event.types";
import { OrderHistoryEventDisplay } from "../order-history-event-display/order-history-event-display.component";

type ActingAdmin = { readonly name: string; readonly email: string } | null | undefined;

const actingAdminLabel = (actingAdmin: ActingAdmin, userId: string | undefined) =>
	actingAdmin ? `${actingAdmin.name} (${actingAdmin.email})` : userId;

export const OrderHistoryEvent = ({ orderHistoryFragmentRef }: OrderHistoryEventProps) => {
	const { t } = useTranslation("common");
	const orderHistory = useFragment<orderHistoryEvent_OrderHistoryFragment$key>(
		ORDER_HISTORY_FRAGMENT,
		orderHistoryFragmentRef,
	);

	return match(orderHistory.kind)
		.with(
			"OrderCreated",
			"CartUpdate",
			"CustomerDetailsUpdate",
			"PaymentMethod",
			"PaymentInProcess",
			"Purchase",
			"PurchaseAwaitingAccount",
			"AccountLinked",
			() => <OrderHistoryEventDisplay event={orderHistory.kind} when={orderHistory.when} />,
		)
		.with("InvoiceSent", () => (
			<OrderHistoryEventDisplay event={orderHistory.kind} when={orderHistory.when}>
				<div>
					<div>{t("order.invoiceId")}:</div>
					<div>{orderHistory.invoiceId}</div>
				</div>
				<div>
					<div>{t("order.invoiceNumber")}:</div>
					<div>{orderHistory.invoiceNumber}</div>
				</div>
			</OrderHistoryEventDisplay>
		))
		.with("InvoiceEmailCorrected", () => (
			<OrderHistoryEventDisplay
				event={orderHistory.kind}
				when={orderHistory.when}
				adminActionTone="adminEmailAction"
			>
				<div>
					<div>{t("order.previousInvoiceEmail")}:</div>
					<div>{orderHistory.previousInvoiceEmail}</div>
				</div>
				<div>
					<div>{t("order.newInvoiceEmail")}:</div>
					<div>{orderHistory.newInvoiceEmail}</div>
				</div>
				<div>
					<div>{t("order.correctedBy")}:</div>
					<div>
						{actingAdminLabel(orderHistory.correctedBy, orderHistory.correctedByUserId)}
					</div>
				</div>
			</OrderHistoryEventDisplay>
		))
		.with("ClaimEmailResent", () => (
			<OrderHistoryEventDisplay
				event={orderHistory.kind}
				when={orderHistory.when}
				adminActionTone="adminEmailAction"
			>
				<div>
					<div>{t("order.sentToInvoiceEmail")}:</div>
					<div>{orderHistory.sentToInvoiceEmail}</div>
				</div>
				<div>
					<div>{t("order.sentBy")}:</div>
					<div>{actingAdminLabel(orderHistory.sentBy, orderHistory.sentByUserId)}</div>
				</div>
			</OrderHistoryEventDisplay>
		))
		.with("InvoiceResent", () => (
			<OrderHistoryEventDisplay
				event={orderHistory.kind}
				when={orderHistory.when}
				adminActionTone="adminEmailAction"
			>
				<div>
					<div>{t("order.invoiceNumber")}:</div>
					<div>{orderHistory.invoiceNumber}</div>
				</div>
				<div>
					<div>{t("order.sentToInvoiceEmail")}:</div>
					<div>{orderHistory.sentToInvoiceEmail}</div>
				</div>
				<div>
					<div>{t("order.sentBy")}:</div>
					<div>{actingAdminLabel(orderHistory.sentBy, orderHistory.sentByUserId)}</div>
				</div>
			</OrderHistoryEventDisplay>
		))
		.with("OrderClosedByAdmin", () => (
			<OrderHistoryEventDisplay
				event={orderHistory.kind}
				when={orderHistory.when}
				adminActionTone="adminCloseOrder"
			>
				<div>
					<div>{t("order.closedReason")}:</div>
					<div>{orderHistory.reason}</div>
				</div>
				<div>
					<div>{t("order.closedBy")}:</div>
					<div>
						{actingAdminLabel(orderHistory.closedBy, orderHistory.closedByUserId)}
					</div>
				</div>
			</OrderHistoryEventDisplay>
		))
		.exhaustive();
};
